import React, { useMemo, useCallback } from 'react'

import { Section, useModalContext } from '@sketch/components'
import { useGetSocialSignIn, useUserProfile } from '@sketch/modules-common'

import { Table } from './SignInMethods.styles'

import { ChangePasswordModal } from '../../../../modals/ChangePasswordModal'
import { ConfirmSocialProviderRemoveModal } from '../../../../modals/ConfirmSocialProviderRemoveModal'
import { AddEmailPasswordMethodModal } from '../../../../modals/AddEmailPasswordMethodModal'
import { AppleRow } from './AppleRow'
import { EmailPasswordRow } from './EmailPasswordRow'
import { GoogleRow } from './GoogleRow'
import { SkeletonRow } from './SkeletonRow'

import {
  GetUserSettingsQuery,
  useGetUserSettingsQuery,
} from '@sketch/gql-types'

// eslint-disable-next-line no-restricted-imports
import { SocialSignInProvider } from '@sketch/gql-types/expansive'
import { useSocialSettingsErrorHandler } from './useSocialSettingsErrorHandler'

// External account management URLs
const APPLE_ACCOUNT_MANAGEMENT_URL = 'https://account.apple.com/account/manage'
const GOOGLE_ACCOUNT_CONNECTIONS_URL =
  'https://myaccount.google.com/connections'

type SocialAccount = GetUserSettingsQuery['me']['socialAccounts'][0]
const EMPTY_SOCIAL_ACCOUNTS: GetUserSettingsQuery['me']['socialAccounts'] = []

export const SignInMethods: React.FC = () => {
  const { data } = useUserProfile()
  const { data: userSettings, loading } = useGetUserSettingsQuery()
  const { showModal } = useModalContext()

  useSocialSettingsErrorHandler()

  const { getSocialAuthStartUrl, loadingState } = useGetSocialSignIn()

  const isAppleLoading =
    loadingState.isLoading && loadingState.provider === 'APPLE'

  const isGoogleLoading =
    loadingState.isLoading && loadingState.provider === 'GOOGLE'

  // Get social accounts from user profile
  const socialAccounts =
    userSettings?.me?.socialAccounts || EMPTY_SOCIAL_ACCOUNTS

  const signInOptions = data?.me?.signInOptions || []
  const userEmail = data?.me?.email

  const hasPasswordSignIn = signInOptions.includes('PASSWORD')

  const appleAccount = useMemo(
    () =>
      socialAccounts.find(
        (account: SocialAccount) => account.provider === 'APPLE'
      ),
    [socialAccounts]
  )

  const googleAccount = useMemo(
    () =>
      socialAccounts.find(
        (account: SocialAccount) => account.provider === 'GOOGLE'
      ),
    [socialAccounts]
  )

  const availableMethods = useMemo(() => {
    return [appleAccount, hasPasswordSignIn, googleAccount].filter(Boolean)
  }, [appleAccount, hasPasswordSignIn, googleAccount])

  const canShowRemove = availableMethods.length > 1

  const handleManageAppleAccount = useCallback(() => {
    window.open(APPLE_ACCOUNT_MANAGEMENT_URL, '_blank')
  }, [])

  const handlePasswordAction = useCallback(() => {
    if (hasPasswordSignIn) {
      showModal(ChangePasswordModal)
    } else {
      showModal(AddEmailPasswordMethodModal)
    }
  }, [hasPasswordSignIn, showModal])

  const handleManageGoogleAccount = useCallback(() => {
    window.open(GOOGLE_ACCOUNT_CONNECTIONS_URL, '_blank')
  }, [])

  const handleRemoveSocialAccount = useCallback(
    (identifier: string, provider: SocialSignInProvider) => {
      showModal(ConfirmSocialProviderRemoveModal, { identifier, provider })
    },
    [showModal]
  )

  const handleAddSocialAccount = useCallback(
    (provider: SocialSignInProvider) => {
      getSocialAuthStartUrl({ variables: { provider, action: 'LINK_ACCOUNT' } })
    },
    [getSocialAuthStartUrl]
  )

  return (
    <Section
      title="Sign in Methods"
      text="Manage alternate login methods for easier and convenient authentication."
    >
      <Table
        header={[{ label: 'Provider' }, { label: 'Action' }]}
        items={
          loading
            ? [
                <SkeletonRow key="skeleton-1" />,
                <SkeletonRow key="skeleton-2" />,
                <SkeletonRow key="skeleton-3" />,
              ]
            : [
                <AppleRow
                  key="apple"
                  account={appleAccount}
                  canRemove={canShowRemove}
                  isLoading={isAppleLoading}
                  onManageAccount={handleManageAppleAccount}
                  onAddAccount={() => handleAddSocialAccount('APPLE')}
                  onRemove={
                    appleAccount
                      ? () =>
                          handleRemoveSocialAccount(
                            appleAccount.identifier,
                            'APPLE'
                          )
                      : undefined
                  }
                />,
                <EmailPasswordRow
                  key="email-password"
                  hasPassword={hasPasswordSignIn}
                  email={userEmail}
                  onPasswordAction={handlePasswordAction}
                />,
                <GoogleRow
                  key="google"
                  account={googleAccount}
                  canRemove={canShowRemove}
                  isLoading={isGoogleLoading}
                  onManageAccount={handleManageGoogleAccount}
                  onAddAccount={() => handleAddSocialAccount('GOOGLE')}
                  onRemove={
                    googleAccount
                      ? () =>
                          handleRemoveSocialAccount(
                            googleAccount.identifier,
                            'GOOGLE'
                          )
                      : undefined
                  }
                />,
              ]
        }
        renderItem={item => item}
        hideHeader
      />
    </Section>
  )
}
